import { postSignupPath } from '../utils/postSignupPath.js';
import { useState, useCallback } from 'react';
import { Capacitor } from '@capacitor/core';
import { SocialLogin } from '@capgo/capacitor-social-login';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import useAuth from '../../context/auth.context.jsx';
import userService from '../services/userService';
import { peekPendingReferral, clearPendingReferral } from '../utils/referral.js';
import Sheet from './common/sheet.jsx';
import Button from './common/button.jsx';
import Input from './common/input.jsx';
import { SocialSignupConsentSheet, hasSignupConsent } from './signupConsent.jsx';

// Sign in with Apple only exists here to satisfy App Store review guideline
// 4.8 (any third-party sign-in must be matched by Apple's own) — so unlike
// Google this never runs on the web or on Android. Android's Apple flow
// needs a backend redirect endpoint we don't have; scope it to iOS only.
const IS_IOS = Capacitor.getPlatform() === 'ios';

// initialize() is idempotent but does real work, so keep one in-flight promise.
let socialLoginInit = null;
function initSocialLogin() {
    if (!socialLoginInit) {
        // No clientId needed here: on iOS the native AuthenticationServices API
        // signs against the app's own bundle id, not a separate Services ID.
        socialLoginInit = SocialLogin.initialize({ apple: {} });
    }
    return socialLoginInit;
}

/** Native sign-in button backed by iOS's own Sign in with Apple sheet. */
export function AppleButton({ onCredential }) {
    const { t } = useTranslation('auth');
    const [busy, setBusy] = useState(false);

    const signIn = async () => {
        setBusy(true);
        try {
            await initSocialLogin();
            const { result } = await SocialLogin.login({
                provider: 'apple',
                options: { scopes: ['email', 'name'] },
            });
            if (result?.idToken) {
                // Apple sends the name only once, on the very first authorization,
                // and never inside the id token itself — capture it here or lose it.
                const name = [result.profile?.givenName, result.profile?.familyName]
                    .filter(Boolean).join(' ') || undefined;
                onCredential(result.idToken, name);
            } else {
                toast.error(t('appleLoginFailed'));
            }
        } catch (error) {
            // Backing out of the Apple sheet is not a failure worth shouting about.
            if (error?.code !== 'USER_CANCELLED') toast.error(t('appleLoginFailed'));
        } finally {
            setBusy(false);
        }
    };

    if (!IS_IOS) return null;
    return (
        <div className="d-flex justify-content-center mt-3">
            <Button text={t('continueWithApple')} onClick={signIn} disabled={busy} />
        </div>
    );
}

/**
 * Full sign-in/sign-up flow for the auth pages, mirroring GoogleAuthFlow:
 * Apple button → token (+ name on first sign-in) → complete phone if needed.
 */
export default function AppleAuthFlow({ signupConsent } = {}) {
    const { refreshUser, loadUserData } = useAuth();
    const navigate = useNavigate();
    const { t } = useTranslation('auth');
    const [needPhone, setNeedPhone] = useState(false);
    // Where to go once sign-in (and the phone step, if any) is done: new accounts start with onboarding.
    const [startPath, setStartPath] = useState('/');
    const [phone, setPhone] = useState('');
    const [busy, setBusy] = useState(false);
    const [pending, setPending] = useState(null);

    const submitCredential = useCallback(async (credential, name, consent) => {
        try {
            const result = await userService.appleLogin(credential, name, peekPendingReferral(), consent);
            clearPendingReferral(); // only after a successful sign-in — see utils/referral.js
            setPending(null);
            const start = result.isNew ? postSignupPath() : '/';
            if (result.needsPhone) {
                setStartPath(start);
                setNeedPhone(true); // token saved; complete profile before entering
            } else {
                refreshUser();
                navigate(start);
            }
        } catch (error) {
            if (error.response?.data?.code === 'SIGNUP_CONSENT_REQUIRED') {
                setPending({ credential, name });
                return;
            }
            toast.error(error.response?.data?.error || t('appleLoginFailed'));
        }
    }, [navigate, refreshUser, t]);

    const handleCredential = useCallback(async (credential, name) => {
        if (signupConsent && !hasSignupConsent(signupConsent)) {
            toast.error(t('consentRequired'));
            return;
        }
        await submitCredential(credential, name, signupConsent);
    }, [signupConsent, submitCredential, t]);

    const skipPhone = () => {
        refreshUser();
        navigate(startPath);
    };

    const savePhone = async () => {
        const cleaned = phone.replace(/\D/g, '');
        if (!/^[0-9]{7,15}$/.test(cleaned)) return toast.error(t('phoneInvalid'));
        setBusy(true);
        try {
            await userService.updateMe({ phone: cleaned });
            await loadUserData();
            refreshUser();
            navigate(startPath);
        } catch (error) {
            toast.error(error.response?.data?.error || t('phoneSaveFailed'));
        } finally {
            setBusy(false);
        }
    };

    if (!IS_IOS) return null;

    return (
        <>
            <AppleButton onCredential={handleCredential} />
            <SocialSignupConsentSheet
                key={pending ? 'apple-consent' : 'apple-consent-closed'}
                open={Boolean(pending)}
                onClose={() => setPending(null)}
                onConfirm={(consent) => submitCredential(pending.credential, pending.name, consent)}
            />

            {/* Skippable — see the same sheet in googleAuth.jsx. */}
            <Sheet open={needPhone} onClose={skipPhone} title={t('oneMoreDetail')}>
                <p className="text-muted-ms">
                    {t('phoneForBot')}
                </p>
                <Input
                    label={t('phone')}
                    name="applePhone"
                    type="tel"
                    inputMode="numeric"
                    placeholder=""
                    value={phone}
                    onChange={e => setPhone(e.target.value)}
                    required
                />
                <Button text={t('saveAndContinue')} onClick={savePhone} disabled={busy} />
                <button type="button" className="btn-ms btn-ms-link btn-ms-block mt-2" onClick={skipPhone}>
                    {t('skipForNow')}
                </button>
            </Sheet>
        </>
    );
}
